/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createClinic,
  deleteClinicById,
  getAllClinics,
  updateClinicById,
  bulkDeleteClinics,
  getClinicById,
  connectToTwilio,
  checkPhoneNumberPortability,
  generateUploadUrl,
  generateReadOnlyUrl,
  uploadFile,
  uploadPortingDocumentToTwilio,
  createPortDocument,
  getPortDocuments,
  createTwilioPortRequest,
  createPortRequest,
  getAllPortRequests,
  getPortRequestById,
  getPortRequestStatusFromTwilio,
  updatePortRequestStatus,
} from '@services/api/clinic.api'
import {
  IClinic,
  IClinicCreateRequest,
  ICreatePortInResponse,
  ITwilioPortDocument,
  ITwilioPortRequest,
} from '@dataTypes/clinic.type'
import { logger } from '@utils/safeLogger'
import { getClinicNumber } from '@utils/clinic.util'
import { PortRequestStatus } from '@dataTypes/twilio.types'
import { BLOB_FOLDERS } from '@constants/Azure'

export const fetchAllClinics = createAsyncThunk<
  IClinic[],
  void,
  { rejectValue: string }
>('api/clinic/fetch/all', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllClinics()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'clinics' in response.data &&
      Array.isArray(response.data.clinics)
    ) {
      const _clinics = response.data.clinics as IClinic[]
      const clinics = _clinics.sort((a, b) =>
        a.date_time_created_at && b.date_time_created_at
          ? new Date(a.date_time_created_at).getTime() -
            new Date(b.date_time_created_at).getTime()
          : 0,
      )
      return (clinics as IClinic[]).map((clinic) => ({
        ...clinic,
        phone_number: getClinicNumber(clinic),
      }))
    }

    // Handle the case where clinics are not in the response
    return rejectWithValue('Clinics not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchClinicById = createAsyncThunk<
  IClinic,
  string,
  { rejectValue: string }
>('api/clinic/fetch/byId', async (clinic_id, { rejectWithValue }) => {
  try {
    const response = await getClinicById(clinic_id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data && 'clinic' in response.data && response.data.clinic) {
      return response.data.clinic as IClinic
    }

    return rejectWithValue('Clinic not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addClinic = createAsyncThunk<
  void,
  IClinicCreateRequest,
  {
    rejectValue: string
  }
>('api/clinic/add', async (clinicData, { dispatch, rejectWithValue }) => {
  try {
    const response = await createClinic(clinicData)

    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add clinic')
    }

    // Refresh the clinics list after successful creation
    dispatch(fetchAllClinics())
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create clinic')
  }
})

export const updateClinic = createAsyncThunk<
  IClinic,
  { clinic_id: string; updates: Partial<IClinic> },
  { rejectValue: string }
>('api/clinic/update', async ({ clinic_id, updates }, { rejectWithValue }) => {
  try {
    const response = await updateClinicById(clinic_id, updates)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    // Type narrowing for `response.data`
    if (
      response.data &&
      'clinic' in response.data &&
      typeof response.data.clinic === 'object' &&
      !Array.isArray(response.data.clinic)
    ) {
      const _clinic = response.data.clinic as IClinic
      return {
        ..._clinic,
        phone_number: getClinicNumber(_clinic),
      }
    }

    return rejectWithValue('Clinic not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'Error occurred during update')
  }
})

export const deleteClinic = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string
  }
>('api/clinic/delete', async (clinic_id, { rejectWithValue }) => {
  try {
    const response = await deleteClinicById(clinic_id)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    return clinic_id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const deleteClinics = createAsyncThunk<
  string[],
  string[],
  {
    rejectValue: string
  }
>('api/clinic/delete/bulk', async (clinic_ids, { rejectWithValue }) => {
  try {
    const response = await bulkDeleteClinics(clinic_ids)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    return clinic_ids
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const twilioAuthentication = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(
  'api/clinic/twilio-authentication',
  async (clinic_id, { rejectWithValue }) => {
    try {
      const response = await connectToTwilio(clinic_id)
      if (response.statusCode !== 200) {
        logger.error('Error in twilioAuthentication', {
          message: response.message,
        })
      }

      window.location.href = response.data.authorize_url
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const checkPortability = createAsyncThunk<
  void,
  string[],
  { rejectValue: string }
>(
  'api/clinic/check-phone-number-portability',
  async (phone_number, { rejectWithValue }) => {
    try {
      const response = await checkPhoneNumberPortability(phone_number)
      if (response.statusCode !== 200) {
        logger.error('Error in checkPhoneNumberPortability', {
          message: response.message,
        })
      }

      logger.info('Phone number portability check completed')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const uploadDocument = createAsyncThunk<
  void,
  {
    file: File
    contentType: string
    filename: string //filename = `tenant/clinic/${document-type}`
    clinicId: string
    documentType: string
  },
  { rejectValue: string }
>('api/clinic/upload-file', async (args, { rejectWithValue }) => {
  try {
    // Step 1: Upload to Azure Storage
    const urlResponse = await generateUploadUrl(
      args.contentType,
      args.filename,
      {
        folder: BLOB_FOLDERS.PORT_DOCUMENTS,
        clinic_id: args.clinicId,
      },
    )
    if (urlResponse.statusCode !== 200) {
      return rejectWithValue(
        `Failed to generate upload URL: ${urlResponse.message}`,
      )
    }

    const azureUploadResponse = await uploadFile(
      urlResponse.data.upload_url,
      args.file,
    )

    if (azureUploadResponse.statusCode !== 200) {
      return rejectWithValue(
        `Failed to upload to Azure: ${azureUploadResponse.message}`,
      )
    }

    // Generate a read-only signed URL for Twilio access (valid for 1 year)
    // This ensures Twilio can access the document when creating port requests
    const readOnlyUrlResponse = await generateReadOnlyUrl(
      urlResponse.data.blob_name || args.filename,
      24 * 365, // 1 year expiration
    )

    if (readOnlyUrlResponse.statusCode !== 200) {
      return rejectWithValue(
        `Failed to generate read-only URL: ${readOnlyUrlResponse.message}`,
      )
    }

    const azureFileUrl = readOnlyUrlResponse.data.read_only_url

    // Step 2: Upload to Twilio
    const twilioUploadResponse = await uploadPortingDocumentToTwilio(
      args.file,
      args.filename,
      args.documentType,
    )

    if (twilioUploadResponse.statusCode !== 200) {
      return rejectWithValue(
        `Failed to upload to Twilio: ${twilioUploadResponse.message}`,
      )
    }

    const twilioDocumentSid = twilioUploadResponse.data.document.sid

    // Step 3: Save to database
    const dbResponse = await createPortDocument(
      args.clinicId,
      twilioDocumentSid,
      args.documentType,
      azureFileUrl,
    )

    if (dbResponse.statusCode !== 201) {
      return rejectWithValue(
        `Failed to save to database: ${dbResponse.message}`,
      )
    }

    logger.info('Document uploaded successfully')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchPortDocuments = createAsyncThunk<
  ITwilioPortDocument[],
  string | undefined,
  { rejectValue: string }
>('api/clinic/fetch-port-documents', async (clinicId, { rejectWithValue }) => {
  try {
    const response = await getPortDocuments(clinicId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data && 'twilio_port_documents' in response.data) {
      return response.data.twilio_port_documents
    }

    return rejectWithValue('Port documents not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const submitPortRequest = createAsyncThunk<
  ITwilioPortRequest,
  {
    clinicId: string
    phoneNumber: string
    phoneCarrier?: string
    adminEmail: string
    userFullname: string
    userEmail: string
    documentIds?: string[]
    companyCustomerName?: string
    customerType?: 'Individual' | 'Business'
    targetPortDate?: string
  },
  { rejectValue: string }
>('api/clinic/create-port-request', async (args, { rejectWithValue }) => {
  try {
    // Step 1: Fetch port documents for the clinic
    const portDocumentsResponse = await getPortDocuments(args.clinicId)
    if (portDocumentsResponse.statusCode !== 200) {
      return rejectWithValue(
        `Failed to fetch port documents: ${portDocumentsResponse.message}`,
      )
    }

    let portDocuments = portDocumentsResponse.data?.twilio_port_documents || []

    // Filter by document_ids if provided
    if (
      args.documentIds &&
      Array.isArray(args.documentIds) &&
      args.documentIds.length > 0
    ) {
      const documentIds = args.documentIds
      portDocuments = portDocuments.filter(
        (doc) =>
          doc.twilio_port_document_id &&
          documentIds.includes(doc.twilio_port_document_id),
      )
    }

    if (portDocuments.length === 0) {
      return rejectWithValue('No port documents found for this clinic')
    }

    // Extract file URLs from documents
    const fileUrls = portDocuments.map((doc) => doc.file_url)

    if (!args.companyCustomerName) {
      return rejectWithValue('companyCustomerName is required')
    }

    // Step 2: Create porting request in Twilio
    const twilioResponse = await createTwilioPortRequest(
      args.phoneNumber,
      fileUrls,
      args.adminEmail,
      args.userFullname,
      args.userEmail,
      args.companyCustomerName,
      args.customerType,
    )

    if (twilioResponse.statusCode !== 201) {
      return rejectWithValue(
        `Failed to create Twilio port request: ${twilioResponse.message}`,
      )
    }

    const twilioPortRequest = twilioResponse.data?.twilio_port_request
    if (!twilioPortRequest) {
      return rejectWithValue('Twilio port request not found in the response')
    }

    const twilioRequestSid = twilioPortRequest.port_in_request_sid
    const status = twilioPortRequest.port_in_request_status

    // Step 3: Save port request to database via business layer
    const dbResponse = await createPortRequest({
      clinic_id: args.clinicId,
      phone_numbers: [args.phoneNumber],
      phone_carrier: args.phoneCarrier || '',
      twilio_port_document_id:
        portDocuments[0]?.twilio_port_document_id || null,
      twilio_request_sid: twilioRequestSid,
      representative_name: args.userFullname,
      representative_email: args.userEmail,
      status: [
        PortRequestStatus.Completed,
        PortRequestStatus.Failed,
        PortRequestStatus.Cancelled,
      ].includes(status as PortRequestStatus)
        ? (status as PortRequestStatus)
        : PortRequestStatus['In Progress'],
      twilio_status: status || 'Pending',
      target_port_date: args.targetPortDate || new Date().toISOString(),
    })

    if (dbResponse.statusCode !== 201) {
      return rejectWithValue(
        `Failed to save port request: ${dbResponse.message}`,
      )
    }

    if (dbResponse.data && 'twilio_port_request' in dbResponse.data) {
      return dbResponse.data.twilio_port_request
    }

    return rejectWithValue('Port request not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchAllPortRequests = createAsyncThunk<
  ITwilioPortRequest[],
  void,
  { rejectValue: string }
>('api/clinic/fetch-all-port-requests', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllPortRequests()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data && 'twilio_port_requests' in response.data) {
      return response.data.twilio_port_requests
    }

    return rejectWithValue('Port requests not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const fetchPortRequestById = createAsyncThunk<
  ITwilioPortRequest,
  string,
  { rejectValue: string }
>(
  'api/clinic/fetch-port-request-by-id',
  async (portRequestId, { rejectWithValue }) => {
    try {
      const response = await getPortRequestById(portRequestId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (response.data && 'twilio_port_request' in response.data) {
        return response.data.twilio_port_request
      }

      return rejectWithValue('Port request not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const syncPortRequestStatus = createAsyncThunk<
  ICreatePortInResponse,
  string,
  { rejectValue: string }
>(
  'api/clinic/sync-port-request-status',
  async (portInRequestSid, { rejectWithValue }) => {
    try {
      const response = await getPortRequestStatusFromTwilio(portInRequestSid)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (response.data && 'twilio_port_request' in response.data) {
        return response.data.twilio_port_request
      }

      return rejectWithValue('Port request status not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const syncAndUpdatePortRequestStatus = createAsyncThunk<
  ITwilioPortRequest,
  {
    portRequestId: string
    portInRequestSid: string
  },
  { rejectValue: string }
>(
  'api/clinic/sync-and-update-port-request-status',
  async ({ portRequestId, portInRequestSid }, { rejectWithValue }) => {
    try {
      // Step 1: Fetch latest status from Twilio
      const twilioResponse =
        await getPortRequestStatusFromTwilio(portInRequestSid)
      if (twilioResponse.statusCode !== 200) {
        return rejectWithValue(
          `Failed to fetch status from Twilio: ${twilioResponse.message}`,
        )
      }

      const twilioPortRequest = twilioResponse.data?.twilio_port_request
      if (!twilioPortRequest) {
        return rejectWithValue(
          'Port request status not found in Twilio response',
        )
      }

      // Step 2: Extract status and rejection reason from Twilio response
      const status = twilioPortRequest.port_in_request_status
      const rejectionReason =
        twilioPortRequest.phone_numbers?.[0]?.rejection_reason || undefined

      // Step 3: Update status in database
      const updateResponse = await updatePortRequestStatus(
        portRequestId,
        status,
        rejectionReason,
      )

      if (updateResponse.statusCode !== 200) {
        return rejectWithValue(
          `Failed to update status in database: ${updateResponse.message}`,
        )
      }

      if (updateResponse.data && 'twilio_port_request' in updateResponse.data) {
        return updateResponse.data.twilio_port_request
      }

      return rejectWithValue('Port request not found in update response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)
